/* Quick Add drawer — registers <quick-add-modal>, the DrawerComponent
   subclass that hydrates theme/sections/quick-add.liquid's template via the
   Section Rendering API. Mirrors quick-view.js (the sanctioned pipeline,
   brief §3/§9) and adds the search-drawer precedent visibility-filtered Tab
   wrap: FoxTheme.a11y.trapFocus derives first/last from ALL focusables,
   including hidden ones, so its wrap can fire from the wrong element. */
if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends DrawerComponent {
      constructor() {
        super();

        this._classes = {
          loaded: 'quick-add-loaded',
        };
        this.drawerBody = this.querySelector(this.selector);

        // Visibility-filtered Tab wrap (search-drawer precedent, QA P1 fix).
        this.addEventListener('keydown', (event) => {
          if (event.code?.toUpperCase() !== 'TAB' || !this.open) return;
          const visible = FoxTheme.a11y.getFocusableElements(this).filter(
            (el) => el.offsetParent !== null || el.getClientRects().length > 0
          );
          if (visible.length === 0) return;
          const first = visible[0];
          const last = visible[visible.length - 1];
          if (!event.shiftKey && event.target === last) {
            event.preventDefault();
            event.stopPropagation();
            first.focus();
          } else if (event.shiftKey && (event.target === first || event.target === this)) {
            event.preventDefault();
            event.stopPropagation();
            last.focus();
          }
        });
      }

      connectedCallback() {
        // Swiper loop:true clones slides, duplicating this host (same id).
        // The slider assets dedupe quick-view-modal with fixQuickviewDuplicate;
        // self-dedupe here so every slider gets it without six patches. Only
        // on FIRST connection — the portal show/hide cycle reconnects this
        // element and getElementById could then resolve to a clone.
        if (!this._qaInitialised) {
          const canonical = document.getElementById(this.id);
          if (canonical && canonical !== this) {
            this.remove();
            return;
          }
          this._qaInitialised = true;
        }
        super.connectedCallback();
      }

      get selector() {
        return '.quick-add__content';
      }

      get sourceSelector() {
        return '#MainProduct-quick-add__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-quick-add'));
      }

      shouleBeShow() {
        const sectionId = this.getProductQuickAddSectionId();
        return typeof sectionId === 'string';
      }

      prepareToShow() {
        super.prepareToShow();
        this.quickadd();
      }

      handleAfterShow() {
        super.handleAfterShow();
        document.dispatchEvent(
          new CustomEvent('quick-add:open', {
            detail: { productUrl: this.dataset.productUrl },
          })
        );
        // U6 handoff (QA P1 fix): ProductForm.showCartDrawer only
        // special-cases quick-view-modal hosts — from a quick-add form it
        // shows the cart drawer WITHOUT hiding this host, and since this is
        // a later body child at the same z-index it paints on top and the
        // shopper gets no success feedback. product-ajax:added fires just
        // before showCartDrawer, so hiding here sequences: quick-add out,
        // cart drawer in on top.
        this._onProductAdded = () => {
          if (this.open) this.hide(true);
        };
        document.addEventListener('product-ajax:added', this._onProductAdded);
      }

      handleAfterHide() {
        super.handleAfterHide();
        if (this._onProductAdded) {
          document.removeEventListener('product-ajax:added', this._onProductAdded);
          this._onProductAdded = null;
        }
        const drawerContent = this.querySelector(this.selector);
        if (drawerContent) drawerContent.innerHTML = '';
        this.classList.remove(this._classes.loaded);
      }

      getProductQuickAddSectionId() {
        let sectionId = FoxTheme.QuickAddSectionId || false;

        if (!sectionId) {
          // Get section id from overlay groups.
          const productQuickAdd = document.querySelector('.section-group-overlay-quick-add');
          if (productQuickAdd) {
            sectionId = FoxTheme.utils.getSectionId(productQuickAdd);
          } else {
            // The section is not (yet) wired into overlay-group.json — fall
            // back to rendering the section file directly by filename via the
            // Section Rendering API. The
            // filename route applies schema defaults, which this section's
            // schema fully specifies.
            sectionId = 'quick-add';
          }

          // Cache for better performance.
          FoxTheme.QuickAddSectionId = sectionId;
        }

        return sectionId;
      }

      quickadd() {
        const drawerContent = this.querySelector(this.selector);
        const sectionId = this.getProductQuickAddSectionId();
        const sectionUrl = `${this.dataset.productUrl.split('?')[0]}?section_id=${sectionId}`;
        fetch(sectionUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const productElement = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector(this.sourceSelector);

            this.setInnerHTML(drawerContent, productElement.content.cloneNode(true));
            FoxTheme.a11y.trapFocus(this, this.focusElement);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            setTimeout(() => {
              this.classList.add(this._classes.loaded);
            }, 300);

            document.dispatchEvent(
              new CustomEvent('quick-add:loaded', {
                detail: { productUrl: this.dataset.productUrl },
              })
            );
          })
          .catch((e) => {
            console.error(e);
          });
      }

      setInnerHTML(element, innerHTML) {
        element.innerHTML = '';
        element.appendChild(innerHTML);
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      disconnectedCallback() {
        // A deduped clone (removed before super.connectedCallback ran) has no
        // abortController; the vendor disconnect would throw on it.
        if (!this.abortController) return;
        super.disconnectedCallback();
        this.handleAfterHide();
      }
    }
  );
}
